/**
 * Authoring a structure: the rules behind "say what this is made of".
 *
 * A bill of materials belongs to a **revision**, and §4.2 says a revision is
 * never edited — a component records which one it was built to, so rewriting
 * one would silently change what an arm built in September claims to be. Taken
 * literally that makes every correction during data entry a new revision, and
 * an afternoon of typing leaves a stack of v2…v7 that are all typos.
 *
 * So the rule here is the one the type form already uses for `kind` and
 * `tracking`: **editable until something depends on it.** A revision nothing
 * has been built to, ordered against, scheduled work for or drawn from stock
 * has no history to protect, and correcting it is just correcting it. The
 * moment any of those exist, the lines are evidence and the next change forks
 * a new revision instead.
 *
 * `mayEditLines` is the whole of that decision, and it is here rather than in
 * a query so that the screen can say *why* before somebody clicks and the
 * server can refuse on the same grounds afterwards.
 */

import { currentRevisionOf, designTree, flattenDesign } from './structure'
import type { DesignSource } from './structure'
import { parsePositionName } from './positions'

/**
 * What already points at a revision.
 *
 * Counted rather than booleans so the screen can say "3 arms" instead of
 * "something", which is the difference between a sentence somebody acts on and
 * one they click past. Attachments are deliberately **not** in this list: a
 * drawing hanging on a revision is what the revision points at, not a claim
 * about its line set.
 */
export interface RevisionDependents {
  /** Components built to it. The one that matters most. */
  readonly builtComponents: number
  readonly buildOrders: number
  /** Maintenance tasks or procedures pinned to this revision. */
  readonly scheduledWork: number
  readonly ledgerEntries: number
  /** Other bills of materials naming it as a child's exact revision. */
  readonly pinnedByLines: number
  readonly superseded: boolean
}

export type FrozenBecause = 'built' | 'ordered' | 'scheduled' | 'stocked' | 'pinned' | 'superseded'

export type LinesVerdict =
  | { readonly editable: true }
  | {
      readonly editable: false
      readonly because: FrozenBecause
      /** How many of them. Zero for `superseded`, which is a state not a count. */
      readonly count: number
    }

export const NO_DEPENDENTS: RevisionDependents = {
  builtComponents: 0,
  buildOrders: 0,
  scheduledWork: 0,
  ledgerEntries: 0,
  pinnedByLines: 0,
  superseded: false,
}

/**
 * May this revision's lines still be changed in place?
 *
 * Reasons are checked in the order a person would find them convincing, so the
 * sentence on screen names the most concrete thing first: five arms built to it
 * beats a stock ledger entry, even though either is enough.
 */
export function mayEditLines(d: RevisionDependents): LinesVerdict {
  if (d.builtComponents > 0) return { editable: false, because: 'built', count: d.builtComponents }
  if (d.buildOrders > 0) return { editable: false, because: 'ordered', count: d.buildOrders }
  if (d.scheduledWork > 0) return { editable: false, because: 'scheduled', count: d.scheduledWork }
  if (d.ledgerEntries > 0) return { editable: false, because: 'stocked', count: d.ledgerEntries }
  if (d.pinnedByLines > 0) return { editable: false, because: 'pinned', count: d.pinnedByLines }
  if (d.superseded) return { editable: false, because: 'superseded', count: 0 }
  return { editable: true }
}

/**
 * The name for the revision a change forks into.
 *
 * `v1` → `v2`, and from any set of names the highest `v<n>` decides. A type
 * whose revisions are called `rev C` gets `v<count + 1>` rather than a clever
 * guess at what follows C — predictable beats smart for something a designer
 * can rename in one field. Uniqueness is the caller's constraint
 * (`UNIQUE (component_type_id, name)`), so it is enforced here rather than
 * discovered as a 409.
 */
export function nextRevisionName(existing: readonly string[]): string {
  const taken = new Set(existing.map((n) => n.trim().toLowerCase()))
  const numbered = existing
    .map((n) => /^v(\d+)$/i.exec(n.trim())?.[1])
    .filter((n): n is string => n !== undefined)
    .map(Number)
  let next = (numbered.length > 0 ? Math.max(...numbered) : existing.length) + 1
  while (taken.has(`v${next}`)) next += 1
  return `v${next}`
}

/**
 * A position name for a child somebody just picked.
 *
 * Clicking a part cannot supply a position, and a serialized child must have
 * one (§2.6) — so one is derived from the type's name and left editable. The
 * first of a kind gets the bare slug and the next gets `_2`, and the first is
 * **not** renamed when the second arrives: a name changing under somebody who
 * has already read it is worse than an inconsistent pair, and `joint_1` /
 * `joint_2` is two keystrokes away regardless.
 *
 * Clamped rather than refused at every edge — a name of punctuation, a name
 * starting with a digit, a name already taken seven times. The caller arrived
 * here by tapping a photograph and should not meet a validation error.
 */
export function positionFor(typeName: string, taken: readonly string[]): string {
  const used = new Set(taken)
  const base = slug(typeName)
  if (!used.has(base)) return base
  for (let n = 2; n < 1000; n += 1) {
    const candidate = `${base}_${n}`
    if (!used.has(candidate)) return candidate
  }
  return `${base}_${Date.now()}`
}

/**
 * The stem a type's positions are named from — `Shoulder actuator` →
 * `shoulder_actuator` — always a valid position name on its own. The same
 * slug `positionFor` uses, exported so a series of them can share it.
 */
export function positionStem(raw: string): string {
  return slug(raw)
}

function slug(raw: string): string {
  const cleaned = raw
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
  // The position grammar is `^[a-z][a-z0-9_]*$`: a leading digit has to be
  // given a letter, and nothing at all has to become something.
  const named = cleaned === '' ? 'part' : cleaned
  const valid = /^[a-z]/.test(named) ? named : `p_${named}`
  return parsePositionName(valid).ok ? valid : 'part'
}

/** The least a candidate list needs to know about a type. */
export interface CandidateType {
  readonly id: string
  readonly name: string
  readonly category: string
  readonly tracking: 'serialized' | 'quantity'
  readonly isRoot: boolean
  readonly supplier?: string
  readonly partNumber?: string
}

/**
 * What this type could be made of — the list the search box draws.
 *
 * Four exclusions, and only the last is a matter of taste:
 *
 *  - **itself**, which `checkBomLine` refuses outright;
 *  - **anything already on the list**, because a picked part moving out of the
 *    search is what makes the list shrink as the work gets done. Adding a
 *    *second* of the same kind — seven joints are seven lines of one actuator
 *    type — is a repeat action on the child row, not a second search;
 *  - **anything whose own structure already contains this type**, which would
 *    be a loop. `checkBomLine` only catches the direct case, and an indirect
 *    one is worse: every walk of that tree afterwards is infinite;
 *  - **robots**, because §4.1 makes a robot the root of a tree and nothing is
 *    fitted into one. That is this function's opinion rather than a rule the
 *    schema holds, and it is here so the list is short rather than correct in
 *    a way nobody needed.
 */
export function childCandidates<T extends CandidateType>(input: {
  readonly types: readonly T[]
  readonly parentTypeId: string
  readonly assignedTypeIds: readonly string[]
  readonly query?: string
  /** Revisions, for the loop check. Omit to skip it. */
  readonly source?: DesignSource
}): readonly T[] {
  const assigned = new Set(input.assignedTypeIds)
  const needle = (input.query ?? '').trim().toLowerCase()
  const wouldLoop = loopGuard(input.parentTypeId, input.source)

  return input.types.filter((t) => {
    if (t.id === input.parentTypeId) return false
    if (t.isRoot) return false
    if (assigned.has(t.id)) return false
    if (wouldLoop(t.id)) return false
    if (needle === '') return true
    return `${t.name} ${t.category} ${t.supplier ?? ''} ${t.partNumber ?? ''}`
      .toLowerCase()
      .includes(needle)
  })
}

/**
 * Would fitting this candidate under the parent close a loop?
 *
 * Built once per call and cached per candidate: the answer needs the
 * candidate's whole subtree, and a list of forty types would otherwise walk
 * forty trees on every keystroke.
 */
function loopGuard(
  parentTypeId: string,
  source: DesignSource | undefined,
): (candidateTypeId: string) => boolean {
  if (source === undefined) return () => false
  const cache = new Map<string, boolean>()
  return (candidateTypeId: string): boolean => {
    const known = cache.get(candidateTypeId)
    if (known !== undefined) return known
    const answer = typesBeneath(candidateTypeId, source).has(parentTypeId)
    cache.set(candidateTypeId, answer)
    return answer
  }
}

/**
 * Every type in a type's own structure, however deep.
 *
 * Reuses `designTree`, which already resolves which revision a line means and
 * already terminates on a loop — so this cannot hang on the bad data it is
 * here to prevent.
 */
export function typesBeneath(typeId: string, source: DesignSource): ReadonlySet<string> {
  const revision = currentRevisionOf(source.revisions, typeId)
  if (revision === undefined) return new Set()
  const tree = designTree(revision.id, source)
  if (tree === null) return new Set()
  return new Set(flattenDesign(tree).map((n) => n.typeId))
}
